/* TiersCrudCard.tsx
	Une card affichant la gestion CRUD des tiers (clients ou fournisseurs)
	Accepte une prop tiersType pour filtrer selon le type ('CLI' pour clients, 'FOU' pour fournisseurs)
	Utilise le composant GenericCrudCard pour lister, ajouter, gérer et supprimer les éléments
	Utilise l'API correspondante pour les opérations de données
*/

// Import React
import React from 'react';

// Import FontAwesome
import { faBuilding, faIndustry } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

// Import Communs
import { GenericCrudCard } from 'common';

// Import Feature
import { TiersData, tiersApi } from 'features/tiers';

// Interface des props du composant
interface TiersCrudCardProps {
	/** 'CLI' pour la vue clients, 'FOU' pour la vue fournisseurs */
	tiersType: 'CLI' | 'FOU';
	size?: 'sm' | 'md' | 'lg';
	returnPath?: string;
	returnLabel?: string;
	returnIcon?: IconDefinition;
	returnInvert?: boolean;
	className?: string;
	/** Affiche ou masque l'icône dans l'en-tête de la card. Défaut : true */
	showIcon?: boolean;
	/** Masque le bouton retour. Utile quand la card est intégrée dans une page avec son propre bouton retour. Défaut : false */
	hideReturn?: boolean;
}

// TiersCrudCard component
export const TiersCrudCard: React.FC<TiersCrudCardProps> = ({
	tiersType,
	size = 'sm',
	returnPath,
	returnLabel,
	returnIcon,
	returnInvert = false,
	className,
	showIcon = true,
	hideReturn = false,
}) => {
	// Dérivation des libellés selon le type de tiers
	const isClient = tiersType === 'CLI';
	const defaultReturnPath = isClient ? '/app/customers' : '/app/suppliers';
	const entityLabel = isClient ? 'client' : 'fournisseur';
	const addPath = isClient
		? '/app/tiers/new?roles=client'
		: '/app/tiers/new?roles=fournisseur';
	const cardTitle =
		size === 'sm'
			? isClient
				? 'Clients'
				: 'Fournisseurs'
			: isClient
				? 'Liste des clients'
				: 'Liste des fournisseurs';

	// Détermination si on utilise la version étendue (size !== 'sm')
	const extended = size !== 'sm';

	// Définition des colonnes du tableau
	const columns = [
		// Colonne "ID" uniquement si extended
		...(extended
			? [
					{
						label: 'ID',
						field: 'id',
						render: (a: TiersData) => `TRS-${a.id}`,
					},
				]
			: []),
		// Colonne "Label"
		{
			label: 'Nom',
			field: 'label',
			render: (a: TiersData) => a.label,
		},
		// Colonne "Type(s)" avec badges colorés (CLI = vert, FOU = bleu)
		{
			label: 'Type(s)',
			field: 'none',
			render: (a: TiersData) => (
				<div className="flex flex-wrap gap-1">
					{(a.types ?? []).map((t) => (
						<span
							key={t.code}
							className={`px-2 py-0.5 rounded-full text-xs font-medium ${
								t.code === 'CLI'
									? 'bg-green-100 text-green-800'
									: 'bg-blue-100 text-blue-800'
							}`}
						>
							{t.label}
						</span>
					))}
				</div>
			),
		},
		// Colonnes supplémentaires en mode étendu
		...(extended
			? [
					// Colonne "Email"
					{
						label: 'Email',
						field: 'email',
						render: (a: TiersData) => a.email ?? '—',
					},
					// Colonne "Téléphone"
					{
						label: 'Téléphone',
						field: 'phone',
						render: (a: TiersData) => a.phone ?? '—',
					},
				]
			: []),
	];

	return (
		<GenericCrudCard<TiersData>
			queryKey={`tiers-${tiersType}`}
			title={cardTitle}
			fetchData={(token, options) =>
				tiersApi.listPaginated(token, {
					...options,
					// Injection du filtre de type pour n'afficher que CLI ou FOU
					filters: { 'types.code': tiersType, ...options?.filters },
				})
			}
			deleteItem={tiersApi.delete}
			tableColumns={columns}
			addButtonLabel={`Ajouter un ${entityLabel}`}
			getAddPath={() => addPath}
			getEditPath={(id) => `/app/tiers/${id}/edit`}
			getManagePath={(id) => `/app/tiers/${id}/manage`}
			emptyMessage={`Aucun ${entityLabel} trouvé.`}
			getReturnPath={
				hideReturn ? undefined : () => returnPath ?? defaultReturnPath
			}
			returnLabel={returnLabel}
			returnIcon={returnIcon}
			returnInvert={returnInvert}
			size={size}
			icon={size === 'sm' ? (isClient ? faBuilding : faIndustry) : undefined}
			className={className}
			showIcon={showIcon}
		/>
	);
};

export default TiersCrudCard;
